import { cx } from "@suse-o11y/class-variance";
import { isFunction } from "@suse-o11y/is-function";
import { mapChildren } from "@suse-o11y/map-children";
import type { OneOrMany } from "@suse-o11y/one-or-many";
import { useHandler } from "@suse-o11y/use-handler";
import { type UseVirtualOptions, type VirtualItem, useVirtual } from "@suse-o11y/use-virtual";
import Empty from "antd/es/empty";
import React from "react";
import { useIntersection } from "react-use";

import { SkeletonList } from "~/components/skeleton/list";

import { VirtualRowContext, useTableContext } from "./_context";
import styles from "./_table.module.css";
import { TableRow } from "./row";

export type TableBodyChildrenRenderer<TValue> = (
	value: TValue,
	index: number,
	arr: ReadonlyArray<TValue>,
) => React.ReactElement;

export interface TableBodyVirtualize<TValue>
	extends Pick<UseVirtualOptions<HTMLElement, HTMLDivElement>, "estimateSize" | "overscan" | "gap" | "scrollMargin"> {
	getItemKey?: (value: undefined | TValue, index: number) => string | number;
}

export interface TableAsyncBodyProps<TValue> {
	className?: string;

	/**
	 * Options for virtualizing the table body rows.
	 */
	virtualize?: TableBodyVirtualize<undefined | TValue>;

	/**
	 * When `each` is not defined it acts as SkeletonList.count - fallbacks as each=new Array(remaining).
	 * Otherwise, it adds the remaining rows to the table as skeletons. Those skeletons trigger onRemainingReveal callback.
	 */
	remaining: number;

	/**
	 * A callback that is triggered when the remaining rows' skeletons are revealed or hidden.
	 */
	onRemainingReveal?: React.Dispatch<boolean>;

	/**
	 * An array of values to render.
	 */
	each: undefined | null | ReadonlyArray<TValue>;

	/**
	 * @default <Empty />
	 */
	empty?: React.ReactNode;

	children: OneOrMany<React.ReactNode | TableBodyChildrenRenderer<undefined | TValue>>;
}

export interface TableSyncBodyProps<TValue> {
	className?: string;

	/**
	 * Options for virtualizing the table body rows.
	 */
	virtualize?: TableBodyVirtualize<TValue>;

	/**
	 * @default <Empty />
	 */
	empty?: React.ReactNode;

	/**
	 * An array of values to render.
	 */
	each: ReadonlyArray<TValue>;
	children: OneOrMany<React.ReactNode | TableBodyChildrenRenderer<TValue>>;
}

export interface TableBodyComponent {
	<TValue>(
		props: React.PropsWithoutRef<TableSyncBodyProps<TValue>> & React.RefAttributes<HTMLDivElement>,
	): React.ReactNode;

	<TValue>(
		props: React.PropsWithoutRef<TableAsyncBodyProps<TValue>> & React.RefAttributes<HTMLDivElement>,
	): React.ReactNode;
}

export const TableBody: TableBodyComponent = React.forwardRef<
	HTMLDivElement,
	TableSyncBodyProps<unknown> | TableAsyncBodyProps<unknown>
>((props, ref) => {
	const { className, remaining, each, empty, virtualize, children, onRemainingReveal, ...rest } =
		"remaining" in props ? props : { ...props, remaining: 0, onRemainingReveal: undefined };

	if (remaining === 0 && each?.length === 0) {
		return (
			<div className={cx("group/empty", styles.group, className)} ref={ref} role="rowgroup" {...rest}>
				<TableRow>
					{empty ?? (
						<Empty
							className="col-span-full m-0! flex h-36 flex-col items-center justify-center bg-white text-center"
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					)}
				</TableRow>
			</div>
		);
	}

	const remainingRenderer = remaining > 0 ? [children].flat().find(isFunction) : undefined;

	return (
		<div className={cx(styles.group, className)} ref={ref} role="rowgroup" {...rest}>
			{virtualize == null ? (
				<>
					<TableRows each={each ?? []}>{children}</TableRows>

					{remainingRenderer && (
						// every time when each size changes, the remaining piece must remount and re-trigger onRemainingReveal
						<TableBodyRemaining
							key={each?.length}
							onRemainingReveal={each == null ? undefined : onRemainingReveal}
							remaining={remaining}
						>
							{remainingRenderer}
						</TableBodyRemaining>
					)}
				</>
			) : (
				<TableVirtualRows
					each={each ?? []}
					onRemainingReveal={each == null ? undefined : onRemainingReveal}
					remaining={remaining}
					virtualize={virtualize}
				>
					{children}
				</TableVirtualRows>
			)}
		</div>
	);
});

interface TableRowsProps<TValue> {
	each: ReadonlyArray<TValue>;
	children: OneOrMany<React.ReactNode | TableBodyChildrenRenderer<TValue>>;
	virtualize?: boolean;
}

const TableRows: <TValue>(props: TableRowsProps<TValue>) => React.ReactNode = ({
	each,
	children,
	virtualize = false,
}) => {
	let rowGroupKey = "";

	const rows = mapChildren(children, (child, childIndex) => {
		if (!isFunction(child)) {
			const key = React.isValidElement(child) && child.key != null ? child.key : childIndex;
			rowGroupKey += `${key}\0`;

			return <React.Fragment key={key}>{child}</React.Fragment>;
		}

		return (
			<React.Fragment key={childIndex}>
				{each.map((value, eachIndex) => {
					const rendered = child(value, eachIndex, each);
					const key = rendered.key ?? eachIndex;

					rowGroupKey += `${key}\0`;

					return <React.Fragment key={key}>{rendered}</React.Fragment>;
				})}
			</React.Fragment>
		);
	});

	/*
		STAC-25086 Interim solution before we move to fully CSS + hook driven approach.

		The key should be undefined when the table is virtualized.

		Otherwise the following render loop can happen:
		1. Key is calculated for a set of items [A, B, C, D]
		2. Table or its elements are resized in some way (e.g. expanding row A)
		3. Height is remeasured and visible items are recalculated (e.g. [A, B], since A is now taking up a larger portion of the viewport)
		4. Row group key changes and causes a remount of the items
		5. The isExpanded initialises as false 
		6. Height is remeasured, so visible virtualized items changes back to [A, B, C, D], causing a key change again
		7. isExpanded state is populated to true
		8. Go to 3
	*/
	return (
		<div className={cx("group/rows", styles.group)} key={virtualize ? undefined : rowGroupKey} role="rowgroup">
			{rows}
		</div>
	);
};

interface TableVirtualRowsProps<TValue> {
	each: ReadonlyArray<TValue>;
	remaining: number;
	onRemainingReveal: undefined | React.Dispatch<boolean>;
	virtualize: TableBodyVirtualize<TValue>;
	children: OneOrMany<React.ReactNode | TableBodyChildrenRenderer<TValue>>;
}

const TableVirtualRows: <TValue>(props: TableVirtualRowsProps<TValue>) => React.ReactNode = ({
	each,
	remaining,
	onRemainingReveal,
	virtualize: { getItemKey, ...virtualize },
	children,
	...props
}) => {
	const { scrollElement } = useTableContext();
	const stableOnRemainingReveal = useHandler(onRemainingReveal);
	const totalCount = each.length + remaining;

	const { getVirtualItems, getTotalSize, measureElement, options } = useVirtual({
		...virtualize,
		getScrollElement: () => scrollElement,
		count: totalCount,
		getItemKey: React.useCallback((index: number) => getItemKey?.(each.at(index), index) ?? index, [getItemKey, each]),
	});

	const virtualItems = getVirtualItems();

	const firstItemStart = virtualItems.at(0)?.start ?? 0;
	const lastItemEnd = virtualItems.at(-1)?.end ?? 0;
	const lastIndex = virtualItems.at(-1)?.index ?? -1;

	/* Signal that skeleton rows are in view so the consumer can fetch the next page.
	   Depends on each.length so it re-fires when a page loads but the user has scrolled
	   far enough that skeletons are still visible. */
	React.useEffect(() => {
		stableOnRemainingReveal?.(remaining > 0 && lastIndex >= each.length);
	}, [stableOnRemainingReveal, remaining, lastIndex, each.length]);

	return (
		<>
			<TableSpacer height={firstItemStart - options.scrollMargin} />

			<TableRows each={virtualItems} virtualize {...props}>
				{mapChildren<
					React.ReactNode | TableBodyChildrenRenderer<unknown>,
					React.ReactNode | TableBodyChildrenRenderer<VirtualItem>
				>(children, (child, _index) => {
					if (!isFunction(child)) {
						return child;
					}

					return ({ index, key }) => {
						const rendered = child(each.at(index), index, each);

						return (
							<VirtualRowContext.Provider key={key} value={key}>
								{React.cloneElement(rendered, { "data-index": index, ref: measureElement })}
							</VirtualRowContext.Provider>
						);
					};
				})}
			</TableRows>

			<TableSpacer height={getTotalSize() - (lastItemEnd - options.scrollMargin)} />
		</>
	);
};

const TableSpacer: React.FC<{
	height: undefined | number;
}> = ({ height }) => <div className="col-span-full bg-white" role="row" style={{ height }} />;

const TableBodyRemaining: React.FC<{
	remaining: number;
	onRemainingReveal: undefined | React.Dispatch<boolean>;
	children: TableBodyChildrenRenderer<undefined>;
}> = ({ remaining, onRemainingReveal, children }) => {
	const stableOnRemainingReveal = useHandler(onRemainingReveal);
	const [remainingNode, setRemainingNode] = React.useState<null | HTMLDivElement>(null);
	const entry = useIntersection(
		{ current: remainingNode },
		{
			threshold: 0,
		},
	);

	React.useEffect(() => {
		if (entry?.isIntersecting != null) {
			stableOnRemainingReveal?.(entry.isIntersecting);
		}
	}, [stableOnRemainingReveal, entry?.isIntersecting]);

	return (
		<SkeletonList
			className={cx("group/skeleton", styles.group)}
			count={remaining}
			each={null}
			el="div"
			ref={setRemainingNode}
			role="rowgroup"
		>
			{children}
		</SkeletonList>
	);
};
